'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetMarkets, useGetSearch } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { SearchGlobeButton } from '@/components/globe/globe-button';
import { SortPillRow } from '@/components/shared/sort-pill-row';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Command, CommandInput } from '@/components/ui/app-command';
import { Icon } from '@/components/ui/icon';
import { CheckmarkCircle02IconSolid, Copy01IconSolid, DollarCircleIcon } from '@/components/ui/icon/glyphs';
import { MotionAnimatedAutoHeight } from '@/components/ui/motion-animated-auto-height';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TOAST_IDS } from '@/config/toast-ids';
import { useSearchQueryMode } from '@/hooks/search/use-search-query-mode';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { TRENDING_SEARCH_LIMIT, TRENDING_SEARCH_STATUS } from '@/lib/sdk/projections/search';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
import { useLayoutStore } from '@/stores/layout';
import { useRecentSearchesStore } from '@/stores/recent-searches';
import type { LiveEventSearchResult } from './live-event-row-search-item';
import { SearchResultsList, type SearchResultsListProps } from './search-modal-results';
import { deriveSearchResultState, searchLoadingState, searchResultRows, useLiveEventSearchCatalog, useSearchActions, useSearchInputState } from './search-modal-state';
import type { SearchTab } from './search-types';
const DIALOG_CONTENT_CLASS = 'max-w-2xl overflow-hidden !p-0 !gap-0';
const MIN_QUERY_LENGTH = 2;

// Reorder per the search-overhaul mockup: All → Markets → Outcomes → Winners →
// Launchpad (proposals) → Users. `winners` is gated by the feed kill-switch.
const BASE_TAB_OPTIONS: ReadonlyArray<SearchTab> = ['all', 'markets', 'outcomes', 'winners', 'proposals', 'users'];
export function SearchModal(t0) {
  const $ = _c(7);
  const {
    liveEvents
  } = t0;
  const searchOpen = useLayoutStore(_temp);
  const searchSession = useLayoutStore(_temp2);
  const searchInitialTab = useLayoutStore(_temp3);
  const setSearchOpen = useLayoutStore(_temp4);
  const liveEventsEnabled = useFeatureGate("live_events") === true;
  const t1 = `${searchSession}:${searchInitialTab}:${liveEventsEnabled}`;
  let t2;
  if ($[0] !== liveEvents || $[1] !== liveEventsEnabled || $[2] !== searchInitialTab || $[3] !== searchOpen || $[4] !== setSearchOpen || $[5] !== t1) {
    t2 = <SearchModalSession key={t1} liveEvents={liveEvents} searchOpen={searchOpen} searchInitialTab={searchInitialTab} setSearchOpen={setSearchOpen} liveEventsEnabled={liveEventsEnabled} />;
    $[0] = liveEvents;
    $[1] = liveEventsEnabled;
    $[2] = searchInitialTab;
    $[3] = searchOpen;
    $[4] = setSearchOpen;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function _temp4(s_2) {
  return s_2.setSearchOpen;
}
function _temp3(s_1) {
  return s_1.searchInitialTab;
}
function _temp2(s_0) {
  return s_0.searchSession;
}
function _temp(s) {
  return s.searchOpen;
}
function SearchModalSession(t0) {
  const $ = _c(31);
  const {
    liveEvents,
    searchOpen,
    searchInitialTab,
    setSearchOpen,
    liveEventsEnabled
  } = t0;
  const t = useTranslations("search");
  const recentEntries = useRecentSearchesStore(_temp5);
  const addRecent = useRecentSearchesStore(_temp6);
  const clearRecent = useRecentSearchesStore(_temp7);
  const {
    query,
    debouncedQuery,
    activeTab,
    setQuery,
    setDebouncedQuery,
    setActiveTab
  } = useSearchInputState(liveEventsEnabled, searchInitialTab);
  const {
    mode,
    cleanQuery
  } = useSearchQueryMode(debouncedQuery);
  const tickerHighlighted = mode === "ticker";
  const shouldSearch = cleanQuery.length >= MIN_QUERY_LENGTH;
  let t1;
  if ($[0] !== cleanQuery || $[1] !== liveEvents || $[2] !== liveEventsEnabled || $[3] !== searchOpen || $[4] !== shouldSearch) {
    t1 = {
      liveEvents,
      liveEventsEnabled,
      searchOpen,
      shouldSearch,
      cleanQuery
    };
    $[0] = cleanQuery;
    $[1] = liveEvents;
    $[2] = liveEventsEnabled;
    $[3] = searchOpen;
    $[4] = shouldSearch;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    matchingLiveEvents,
    eventCatalogPending,
    eventCatalogError,
    retryEventCatalog
  } = useLiveEventSearchCatalog(t1);
  let t2;
  if ($[6] !== cleanQuery) {
    t2 = {
      q: cleanQuery,
      type: "all"
    };
    $[6] = cleanQuery;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = shouldSearch && searchOpen && activeTab !== "live-events";
  let t4;
  if ($[8] !== t3) {
    t4 = {
      enabled: t3
    };
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    data,
    isPending
  } = useGetSearch(t2, t4);
  const {
    blended,
    markets,
    outcomes,
    winners,
    proposals,
    creators,
    users
  } = searchResultRows(data);
  let t5;
  if ($[10] !== activeTab || $[11] !== eventCatalogError || $[12] !== eventCatalogPending || $[13] !== isPending || $[14] !== mode || $[15] !== shouldSearch) {
    t5 = searchLoadingState(activeTab, mode, shouldSearch, isPending, eventCatalogPending, eventCatalogError);
    $[10] = activeTab;
    $[11] = eventCatalogError;
    $[12] = eventCatalogPending;
    $[13] = isPending;
    $[14] = mode;
    $[15] = shouldSearch;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const {
    eventsRelevant,
    searchPending,
    isLoading
  } = t5;
  const {
    hasResults,
    hasExactMatch,
    exactMatchPubkey,
    activeTabHasResults
  } = deriveSearchResultState({
    blended,
    markets,
    outcomes,
    winners,
    proposals,
    creators,
    users,
    liveEvents: matchingLiveEvents
  }, activeTab, mode, cleanQuery);
  useEffect(() => {
    if (hasExactMatch) {
      showToast({
        id: TOAST_IDS.SEARCH_EXACT_MATCH,
        message: t("exact_match_found"),
        variant: "success"
      });
    }
  }, [hasExactMatch, t]);
  const showFuzzyGroups = activeTab === "all" && mode !== "ca";
  const marketByPubkey = new Map(markets.map(_temp8));
  const proposalById = new Map(proposals.map(_temp9));
  let t6;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      sortBy: "createdAt",
      sortOrder: "desc",
      status: TRENDING_SEARCH_STATUS
    };
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== searchOpen) {
    t7 = {
      enabled: searchOpen
    };
    $[18] = searchOpen;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const {
    data: trendingPage
  } = useGetMarkets(t6, t7);
  let t8;
  if ($[20] !== trendingPage?.data) {
    t8 = trendingPage?.data ?? [];
    $[20] = trendingPage?.data;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t8) {
    t9 = t8.slice(0, TRENDING_SEARCH_LIMIT);
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const trendingMarkets = t9;
  const showTrending = trendingMarkets.length > 0;
  let t10;
  if ($[24] !== addRecent || $[25] !== query || $[26] !== setDebouncedQuery || $[27] !== setQuery || $[28] !== setSearchOpen || $[29] !== tickerHighlighted) {
    t10 = {
      query,
      tickerHighlighted,
      addRecent,
      setQuery,
      setDebouncedQuery,
      setSearchOpen
    };
    $[24] = addRecent;
    $[25] = query;
    $[26] = setDebouncedQuery;
    $[27] = setQuery;
    $[28] = setSearchOpen;
    $[29] = tickerHighlighted;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const {
    rowShared,
    commitSearch,
    handleRecentSelect,
    handleOpenChange,
    handlePaste
  } = useSearchActions(t10);
  const results = {
    shouldSearch,
    isLoading,
    isPending: searchPending,
    activeTab,
    activeTabHasResults,
    hasResults,
    hasExactMatch,
    exactMatchPubkey,
    showFuzzyGroups,
    mode,
    tickerHighlighted,
    blended,
    markets,
    outcomes,
    winners,
    proposals,
    creators,
    users,
    liveEvents: matchingLiveEvents,
    marketByPubkey,
    proposalById,
    recentEntries,
    trendingMarkets,
    showTrending,
    rowShared,
    onRecentSelect: handleRecentSelect,
    onClearRecent: clearRecent,
    onCommit: commitSearch,
    onHoverMarket: rowShared.onHoverMarket,
    onHoverWinner: rowShared.onHoverWinner
  };
  return <SearchModalSurface searchOpen={searchOpen} onOpenChange={handleOpenChange} query={query} onQueryChange={setQuery} onPaste={handlePaste} onTabChange={setActiveTab} liveEventsEnabled={liveEventsEnabled} eventsRelevant={eventsRelevant} eventCatalogError={eventCatalogError} onRetryEventCatalog={retryEventCatalog} results={results} />;
}
function _temp9(p) {
  return [p.proposalId, p];
}
function _temp8(m) {
  return [m.pubkey, m];
}
function _temp7(s_1) {
  return s_1.clearRecent;
}
function _temp6(s_0) {
  return s_0.addRecent;
}
function _temp5(s) {
  return s.entries;
}
interface SearchModalSurfaceProps {
  searchOpen: boolean;
  onOpenChange: (open: boolean) => void;
  query: string;
  onQueryChange: (query: string) => void;
  onPaste: () => Promise<void>;
  onTabChange: (tab: SearchTab) => void;
  liveEventsEnabled: boolean;
  eventsRelevant: boolean;
  eventCatalogError: boolean;
  onRetryEventCatalog: () => void;
  results: SearchResultsListProps;
}
function SearchModalSurface(t0) {
  const $ = _c(116);
  const {
    searchOpen,
    onOpenChange: handleOpenChange,
    query,
    onQueryChange: setQuery,
    onPaste: handlePaste,
    onTabChange: setActiveTab,
    liveEventsEnabled,
    eventsRelevant,
    eventCatalogError,
    onRetryEventCatalog: retryEventCatalog,
    results
  } = t0;
  const t = useTranslations("search");
  const tLiveEvents = useTranslations("liveEvents.toolbar");
  const tEventBoundary = useTranslations("liveEvents.feedBoundary");
  const {
    hasResults,
    activeTabHasResults,
    isLoading,
    activeTab,
    recentEntries,
    showTrending,
    mode,
    shouldSearch,
    isPending: searchPending
  } = results;
  const isNoResultsState = shouldSearch && !searchPending && !hasResults;
  let T0;
  let T1;
  let T2;
  let T3;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== activeTab || $[1] !== activeTabHasResults || $[2] !== handleOpenChange || $[3] !== handlePaste || $[4] !== hasResults || $[5] !== isLoading || $[6] !== isNoResultsState || $[7] !== liveEventsEnabled || $[8] !== mode || $[9] !== query || $[10] !== recentEntries.length || $[11] !== searchOpen || $[12] !== setQuery || $[13] !== showTrending || $[14] !== t || $[15] !== tLiveEvents) {
    const tabOptions = liveEventsEnabled ? [...BASE_TAB_OPTIONS.slice(0, 2), "live-events" as const, ...BASE_TAB_OPTIONS.slice(2)] : BASE_TAB_OPTIONS;
    T3 = SilkSheet;
    t10 = searchOpen;
    t11 = handleOpenChange;
    t12 = "detached";
    t13 = "lg";
    t14 = true;
    if ($[39] !== t) {
      t15 = t("title");
      $[39] = t;
      $[40] = t15;
    } else {
      t15 = $[40];
    }
    if ($[41] !== t) {
      t16 = t("description");
      $[41] = t;
      $[42] = t16;
    } else {
      t16 = $[42];
    }
    t17 = true;
    t18 = false;
    t19 = DIALOG_CONTENT_CLASS;
    T2 = MotionAnimatedAutoHeight;
    if ($[43] !== activeTab || $[44] !== activeTabHasResults || $[45] !== hasResults || $[46] !== isLoading || $[47] !== isNoResultsState || $[48] !== mode || $[49] !== recentEntries.length || $[50] !== showTrending) {
      t9 = [hasResults, activeTabHasResults, isLoading, isNoResultsState, activeTab, recentEntries.length, showTrending, mode];
      $[43] = activeTab;
      $[44] = activeTabHasResults;
      $[45] = hasResults;
      $[46] = isLoading;
      $[47] = isNoResultsState;
      $[48] = mode;
      $[49] = recentEntries.length;
      $[50] = showTrending;
      $[51] = t9;
    } else {
      t9 = $[51];
    }
    T1 = Command;
    t6 = false;
    if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = cn("flex flex-col pt-2", "**:data-[slot=command-input-wrapper]:border-b");
      $[52] = t7;
    } else {
      t7 = $[52];
    }
    let t20;
    if ($[53] !== handlePaste) {
      t20 = () => {
        handlePaste();
      };
      $[53] = handlePaste;
      $[54] = t20;
    } else {
      t20 = $[54];
    }
    if ($[55] !== mode || $[56] !== query || $[57] !== setQuery || $[58] !== t20) {
      t8 = <SearchInputRow query={query} mode={mode} onQueryChange={setQuery} onPaste={t20} />;
      $[55] = mode;
      $[56] = query;
      $[57] = setQuery;
      $[58] = t20;
      $[59] = t8;
    } else {
      t8 = $[59];
    }
    t5 = "flex items-center gap-2 px-3 py-2";
    t4 = "min-w-0 flex-1";
    T0 = SortPillRow;
    t1 = "sm";
    t2 = "search-modal-active-tab";
    let t21;
    if ($[60] !== t || $[61] !== tLiveEvents) {
      t21 = tab => ({
        value: tab,
        label: tab === "live-events" ? tLiveEvents("title") : t(`tab_${tab}`)
      });
      $[60] = t;
      $[61] = tLiveEvents;
      $[62] = t21;
    } else {
      t21 = $[62];
    }
    t3 = tabOptions.map(t21);
    $[0] = activeTab;
    $[1] = activeTabHasResults;
    $[2] = handleOpenChange;
    $[3] = handlePaste;
    $[4] = hasResults;
    $[5] = isLoading;
    $[6] = isNoResultsState;
    $[7] = liveEventsEnabled;
    $[8] = mode;
    $[9] = query;
    $[10] = recentEntries.length;
    $[11] = searchOpen;
    $[12] = setQuery;
    $[13] = showTrending;
    $[14] = t;
    $[15] = tLiveEvents;
    $[16] = T0;
    $[17] = T1;
    $[18] = T2;
    $[19] = T3;
    $[20] = t1;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
    $[28] = t17;
    $[29] = t18;
    $[30] = t19;
    $[31] = t2;
    $[32] = t3;
    $[33] = t4;
    $[34] = t5;
    $[35] = t6;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
  } else {
    T0 = $[16];
    T1 = $[17];
    T2 = $[18];
    T3 = $[19];
    t1 = $[20];
    t10 = $[21];
    t11 = $[22];
    t12 = $[23];
    t13 = $[24];
    t14 = $[25];
    t15 = $[26];
    t16 = $[27];
    t17 = $[28];
    t18 = $[29];
    t19 = $[30];
    t2 = $[31];
    t3 = $[32];
    t4 = $[33];
    t5 = $[34];
    t6 = $[35];
    t7 = $[36];
    t8 = $[37];
    t9 = $[38];
  }
  let t20;
  if ($[63] !== setActiveTab) {
    t20 = value => setActiveTab(value as SearchTab);
    $[63] = setActiveTab;
    $[64] = t20;
  } else {
    t20 = $[64];
  }
  let t21;
  if ($[65] !== T0 || $[66] !== activeTab || $[67] !== t1 || $[68] !== t2 || $[69] !== t20 || $[70] !== t3) {
    t21 = <T0 size={t1} layoutId={t2} options={t3} activeValue={activeTab} onChange={t20} />;
    $[65] = T0;
    $[66] = activeTab;
    $[67] = t1;
    $[68] = t2;
    $[69] = t20;
    $[70] = t3;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  let t22;
  if ($[72] !== t21 || $[73] !== t4) {
    t22 = <div className={t4}>{t21}</div>;
    $[72] = t21;
    $[73] = t4;
    $[74] = t22;
  } else {
    t22 = $[74];
  }
  let t23;
  if ($[75] !== activeTab) {
    t23 = <SearchGlobeButton activeTab={activeTab} />;
    $[75] = activeTab;
    $[76] = t23;
  } else {
    t23 = $[76];
  }
  let t24;
  if ($[77] !== t22 || $[78] !== t23 || $[79] !== t5) {
    t24 = <div className={t5}>{t22}{t23}</div>;
    $[77] = t22;
    $[78] = t23;
    $[79] = t5;
    $[80] = t24;
  } else {
    t24 = $[80];
  }
  let t25;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="border-t border-border-primary" />;
    $[81] = t25;
  } else {
    t25 = $[81];
  }
  let t26;
  if ($[82] !== eventCatalogError || $[83] !== eventsRelevant || $[84] !== retryEventCatalog || $[85] !== shouldSearch || $[86] !== tEventBoundary) {
    t26 = shouldSearch && eventsRelevant && eventCatalogError ? <div className="flex items-center justify-between gap-3 px-4 py-3 text-caption text-foreground-secondary"><span>{tEventBoundary("errorTitle")}</span><AnimatedButton size="xs" variant="tertiary" onClick={() => {
        retryEventCatalog();
      }}>{tEventBoundary("retry")}</AnimatedButton></div> : null;
    $[82] = eventCatalogError;
    $[83] = eventsRelevant;
    $[84] = retryEventCatalog;
    $[85] = shouldSearch;
    $[86] = tEventBoundary;
    $[87] = t26;
  } else {
    t26 = $[87];
  }
  let t27;
  if ($[88] !== results) {
    t27 = <SearchResultsList {...results} />;
    $[88] = results;
    $[89] = t27;
  } else {
    t27 = $[89];
  }
  let t28;
  if ($[90] !== T1 || $[91] !== t24 || $[92] !== t26 || $[93] !== t27 || $[94] !== t6 || $[95] !== t7 || $[96] !== t8) {
    t28 = <T1 shouldFilter={t6} className={t7}>{t8}{t24}{t25}{t26}{t27}</T1>;
    $[90] = T1;
    $[91] = t24;
    $[92] = t26;
    $[93] = t27;
    $[94] = t6;
    $[95] = t7;
    $[96] = t8;
    $[97] = t28;
  } else {
    t28 = $[97];
  }
  let t29;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <SearchFooter />;
    $[98] = t29;
  } else {
    t29 = $[98];
  }
  let t30;
  if ($[99] !== T2 || $[100] !== t28 || $[101] !== t9) {
    t30 = <T2 layoutDependencies={t9}>{t28}{t29}</T2>;
    $[99] = T2;
    $[100] = t28;
    $[101] = t9;
    $[102] = t30;
  } else {
    t30 = $[102];
  }
  let t31;
  if ($[103] !== T3 || $[104] !== t10 || $[105] !== t11 || $[106] !== t12 || $[107] !== t13 || $[108] !== t14 || $[109] !== t15 || $[110] !== t16 || $[111] !== t17 || $[112] !== t18 || $[113] !== t19 || $[114] !== t30) {
    t31 = <T3 open={t10} onOpenChange={t11} variant={t12} size={t13} keyboard={t14} title={t15} description={t16} srOnlyHeader={t17} closeButton={t18} contentClassName={t19}>{t30}</T3>;
    $[103] = T3;
    $[104] = t10;
    $[105] = t11;
    $[106] = t12;
    $[107] = t13;
    $[108] = t14;
    $[109] = t15;
    $[110] = t16;
    $[111] = t17;
    $[112] = t18;
    $[113] = t19;
    $[114] = t30;
    $[115] = t31;
  } else {
    t31 = $[115];
  }
  return t31;
}
interface SearchInputRowProps {
  query: string;
  mode: string;
  onQueryChange: (value: string) => void;
  onPaste: () => void;
}
function SearchInputRow(t0) {
  const $ = _c(27);
  const {
    query,
    mode,
    onQueryChange,
    onPaste
  } = t0;
  const t = useTranslations("search");
  let t1;
  if ($[0] !== t) {
    t1 = t("placeholder");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onQueryChange || $[3] !== query || $[4] !== t1) {
    t2 = <CommandInput placeholder={t1} value={query} onValueChange={onQueryChange} className="pr-28" />;
    $[2] = onQueryChange;
    $[3] = query;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== mode || $[7] !== t) {
    t3 = mode === "ca" ? <span className="inline-flex h-6 items-center gap-1 rounded-pill bg-background-brand px-2 text-micro font-medium text-foreground-brand"><Icon icon={CheckmarkCircle02IconSolid} className="size-3" />{t("mode_ca")}</span> : null;
    $[6] = mode;
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== mode || $[10] !== t) {
    t4 = mode === "ticker" ? <span className="inline-flex h-6 items-center gap-1 rounded-pill bg-feedback-warning-accent/10 px-2 text-micro font-medium text-feedback-warning-content"><Icon icon={DollarCircleIcon} className="size-3" />{t("mode_ticker")}</span> : null;
    $[9] = mode;
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== onPaste) {
    t5 = () => {
      onPaste();
    };
    $[12] = onPaste;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Icon icon={Copy01IconSolid} className="size-3" />;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t) {
    t7 = t("paste");
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t5 || $[18] !== t7) {
    t8 = <AnimatedButton size="xs" variant="tertiary" className="border" onClick={t5}>{t6}{t7}</AnimatedButton>;
    $[17] = t5;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t3 || $[21] !== t4 || $[22] !== t8) {
    t9 = <div className="absolute right-3 top-0 flex h-9 items-center gap-2">{t3}{t4}{t8}</div>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t2 || $[25] !== t9) {
    t10 = <div className="relative">{t2}{t9}</div>;
    $[24] = t2;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}
function SearchFooter() {
  const $ = _c(7);
  const t = useTranslations("search");
  let t0;
  if ($[0] !== t) {
    t0 = t("close_hint");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <span className="text-caption text-foreground-secondary">{t0}</span>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <kbd className="rounded border border-border-primary bg-background-tertiary px-1.5 py-0.5 text-micro font-medium text-foreground-secondary">ESC</kbd>;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1) {
    t3 = <div className="hidden items-center justify-between border-t border-border-primary px-4 py-2 sm:flex">{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}